import { IconButton, Stack, TextField, styled } from '@mui/material';
import { useEffect, useState } from 'react';
import { useThrottle } from 'react-use';
import { UserStatus as userStatusType } from 'src/@types/user-status';
import { useAuthContext } from 'src/auth/useAuthContext';
import { EmojiHandlerPopover } from 'src/components/EmojiPickerPopover';
import { Iconify } from 'src/components/iconify';
import { useLocales } from 'src/locales';
import { UserStatusOptions } from '../UserStatusOptions';
import { useUpdateUserStatusMutation } from './useUpdateUserStatusMutation';

export const UserCustomStatusChanger = () => {
  const { user } = useAuthContext();
  const { translate } = useLocales();
  const { updateUserStatus, isUpdatingUserStatus } =
    useUpdateUserStatusMutation();

  const [customStatusText, setCustomStatusText] = useState(
    user?.customStatus?.text || '',
  );

  const [selectedStatus, setSelectedStatus] = useState(
    user?.customStatus?.status || user?.status || userStatusType.ONLINE,
  );

  const onSelectEmoji = (emoji: string) => {
    updateUserStatus({
      ...user?.customStatus,
      icon: emoji,
    });
  };

  const handleStatusChange = async (status: userStatusType) => {
    if (!status || isUpdatingUserStatus) return;

    setSelectedStatus(status);
    updateUserStatus({
      ...user?.customStatus,
      status,
    });
  };

  const handleRemoveCustomStatus = () => {
    updateUserStatus({ ...user?.customStatus, icon: '', text: '' });
    setCustomStatusText('');
  };

  const throttledValue = useThrottle(customStatusText, 3000);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (throttledValue !== user?.customStatus?.text && !isUpdatingUserStatus) {
      updateUserStatus({ ...user?.customStatus, text: throttledValue });
    }
  }, [throttledValue]);

  return (
    <Stack pb={1}>
      <Stack direction="row" alignItems="center" py={1} px={2}>
        <EmojiHandlerPopover
          icon={user?.customStatus?.icon}
          onSelect={onSelectEmoji}
          size="small"
        />

        <StyledTextField
          id="outlined-start-adornment"
          variant="outlined"
          placeholder={translate('header.profile_dropdown.set_custom_status')}
          value={customStatusText}
          onChange={(e) => setCustomStatusText(e.target.value)}
          size="small"
          fullWidth
        />
        {(user?.customStatus.text?.length || !!user?.customStatus?.icon) && (
          <StyledIconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              event.preventDefault();
              handleRemoveCustomStatus();
            }}
          >
            <Iconify icon="tabler:x" />
          </StyledIconButton>
        )}
      </Stack>
      <UserStatusOptions
        handleStatusChange={handleStatusChange}
        selectedStatus={selectedStatus}
      />
    </Stack>
  );
};

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: 'transparent',
    },
    '&:hover fieldset': {
      borderColor: 'transparent',
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.divider,
    },
  },
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  height: theme.spacing(3.25),
  width: theme.spacing(3.25),
  '&:hover': {
    color: theme.palette.text.primary,
  },
}));
